/**
 * A rave's bios in the reader's language, offline too (step 76.10; the user, 2026-10-29: "add it to the
 * offline mode as well — offline mode does not need to download all languages, just the one used by the app").
 *
 * The server translates every bio once when it is saved; `GET /editions/:id/bios?lang=` answers one rave's in
 * ONE language. This keeps that answer on the phone (`myrave:{editionId}:bios-<lang>`) — signed in only, like
 * every offline copy (signed out is online only) — and `withRaveBios` lays it over the line-up as each
 * performer's `bioLocal[lang]`, so the set card and the artist pop-up read it like a translation a person
 * wrote, with no signal. A bio Google translated carries `bioByGoogle` for its "Translated by Google".
 */
import { useQuery } from '@tanstack/react-query';
import type { EditionBundle } from '@myrave/shared';
import { endpoints, type RaveBios } from '@/lib/api';
import { readJson, writeJson } from '@/lib/storage';
import { signedInHere } from '@/lib/signOut';

const keyFor = (language: string) => `bios-${language}`;

/** The rave's translated bios in `language`: the copy on the phone at once, the server's when it answers. */
export function useRaveBios(editionId: string, language: string, enabled: boolean): RaveBios | null {
  const kept = enabled && signedInHere() ? readJson<RaveBios | null>(editionId, keyFor(language), null) : null;
  const asked = useQuery({
    queryKey: ['rave-bios', editionId, language],
    queryFn: async () => {
      const { bios } = await endpoints.raveBios(editionId, language);
      if (signedInHere()) writeJson(editionId, keyFor(language), bios);
      return bios;
    },
    enabled: enabled && /^[a-z]{2}$/.test(language),
    staleTime: 30 * 60_000,
    retry: false,
  });
  return asked.data ?? kept;
}

/** The line-up with these bios as each performer's translation — a person's own (`bioLocal`) is never replaced. */
export function withRaveBios(bundle: EditionBundle, bios: RaveBios | null, language: string): EditionBundle {
  if (!bios || !bundle.artists?.length) return bundle;
  let changed = false;
  const artists = bundle.artists.map((a) => {
    const b = bios[a.id];
    if (!b || a.bioLocal?.[language]) return a;
    changed = true;
    return { ...a, bioLocal: { ...(a.bioLocal ?? {}), [language]: b.text }, ...(b.byGoogle ? { bioByGoogle: true } : {}) };
  });
  return changed ? { ...bundle, artists } : bundle;
}
